import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import WaitingRoomFooter from './WaitingRoomFooter';
import StickyFooterLayout from 'common/StickyFooterLayout';
import WaitingRoom from './WaitingRoom';
import WaitingRoomHistory from './WaitingRoomHistory';
import ErrorMessage from 'common/ErrorMessage';
import WarningMessage from 'common/WarningMessage';
import useEventListener from '@use-it/event-listener';

function App(props) {
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  // Store the current route in the state
  const getCurrentRoute = () => location.hash.replace("#", "");
  const [lastRoute, setLastRoute] = useState(getCurrentRoute());

  // Whenever a route change, we store the new hash in the state if not
  // the student folder. This way we can refer back to it when the folder pops up
  useEventListener('hashchange', () => {
    const route = getCurrentRoute();
    if ( route !== "/studentFolder" ) setLastRoute(route);
    else history.replaceState(null, null, `#${lastRoute}`);
  });

  const renderWaitingRoom = () => (
    <WaitingRoom 
      setError={setError} 
      setWarning={setWarning} 
      removeFromQueue={props.removeFromQueue} 
      startMeeting={props.startMeeting}
      editDescription={props.editDescription}
      editAppointment={props.editAppointment}
      handleGroupSession={props.handleGroupSession}
      openStudentFolder={props.openStudentFolder}
    />
  );

  const renderWaitingRoomHistory = () => (
    <WaitingRoomHistory 
      openStudentFolder={props.openStudentFolder}
      editAppointment={props.editAppointment}
      handleGroupSession={props.handleGroupSession}
      formatDate={props.formatDate}
      setError={setError}
    />
  );

  // If the current route is history, render the waiting room history. Otherwise renders the home
  const renderStudentFolder = () => lastRoute === "/history" ? renderWaitingRoomHistory() : renderWaitingRoom();
  
  const getContent = () => (
    <>
      {error && <ErrorMessage message={error} />}
      {warning && <WarningMessage message={warning} />}
      <Router>
        <Switch>
          <Route exact path="/" render={renderWaitingRoom} />
          <Route exact path="/history" render={renderWaitingRoomHistory} />
          <Route exact path="/studentFolder" render={renderStudentFolder} />
          <Route render={() => <h1>Error 404 - Page not found</h1>} />
        </Switch>
      </Router>
    </>
  );

  return <StickyFooterLayout content={getContent()}
    footer={<WaitingRoomFooter
      openAddOfficeHoursModal={props.openAddOfficeHoursModal}
      openAddAppointmentModal={props.openAddAppointmentModal}
      openAddWalkInModal={props.openAddWalkInModal}
      openAddGroupSessionModal={props.openAddGroupSessionModal}
      openReserveTimeModal={props.openReserveTimeModal}
      openSchedulingWizardModal={props.openSchedulingWizardModal}
      userCalendarIsLocked={props.userCalendarIsLocked}
      managesUnlockedCalendar={props.managesUnlockedCalendar}
    />}
  />
}

App.propTypes = {
  openAddOfficeHoursModal: PropTypes.func.isRequired,
  openAddAppointmentModal: PropTypes.func.isRequired,
  openAddWalkInModal: PropTypes.func.isRequired,
  openAddGroupSessionModal: PropTypes.func.isRequired,
  openReserveTimeModal: PropTypes.func.isRequired,
  openSchedulingWizardModal: PropTypes.func.isRequired,
  removeFromQueue: PropTypes.func.isRequired,
  startMeeting: PropTypes.func.isRequired,
  editDescription: PropTypes.func.isRequired,
  editAppointment: PropTypes.func.isRequired,
  handleGroupSession: PropTypes.func.isRequired,
  openStudentFolder: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  userCalendarIsLocked: PropTypes.bool.isRequired,
  managesUnlockedCalendar: PropTypes.bool.isRequired,
};

export default hot(App);
